// @ts-nocheck
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import Link, { LinkPresentationProps, LinkProps } from "@store/MobiusOverrides/Link/Link";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import getFlexAlignment from "@toolkit/Framework/Common/Utilities/getFlexAlignment";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import clearEmptyChild from "@toolkit/Framework/Common/Utilities/react/clearEmptyChild";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import React, { Children, FC, HTMLAttributes, useMemo, useRef } from "react";
import { css } from "styled-components";

export interface HeadingPresentationProps extends ExpandStyles<HeadingStyles> {
    forwardAs?: TypographyProps["forwardAs"];
    variant?: TypographyProps["variant"];
    textAlign?: "left" | "center" | "right";
    verticalAlign?: "top" | "middle" | "bottom";
    itemOffset?: number | null;
}

export interface HeadingProps extends HeadingPresentationProps, HTMLAttributes<HTMLElement> {
    usePageTitle?: boolean;
    title?: string;
    className?: string;
    image?: Pick<LazyImageProps, "src" | "altText">;
    link?: Pick<LinkProps, "href" | "onClick" | "target">;
}

export interface HeadingStyles {
    title?: TypographyPresentationProps;
    flexWrapper?: FlexWrapperPresentationProps;
    itemFlexWrapper?: FlexWrapperPresentationProps;
    image?: LazyImageProps;
    link?: LinkPresentationProps;
}

export const headingStyles: HeadingStyles = {
    flexWrapper: {
        gap: pxToRem(24),
        verticalGap: pxToRem(8),
        extendedStyles: {
            content: {
                css: css`
                    justify-content: space-between;
                `,
            },
            item: {
                css: css`
                    &:first-child {
                        flex-grow: 1;
                    }
                `,
            },
        },
    },
    itemFlexWrapper: {
        gap: pxToRem(16),
        verticalGap: pxToRem(8),
        extendedStyles: {
            content: {
                css: css`
                    ${x => x.theme.typography.body.css}
                    text-align: left;
                `,
            },
            item: {
                css: css`
                    > [data-contentid] {
                        min-width: 125px; /* CMS Edit Fix */
                    }
                `,
            },
        },
    },
};

const Heading: FC<HeadingProps> = ({
    children: baseChildren,
    forwardAs,
    variant,
    title,
    usePageTitle,
    textAlign,
    verticalAlign = "bottom",
    itemOffset = 0.7,
    className,
    extendedStyles,
    overridedStyles,
    image,
    link,
    ...htmlAttributes
}) => {
    const styles = useExpandProps(headingStyles, extendedStyles, overridedStyles);
    const currentPage = useCurrentPage();
    const children = clearEmptyChild(baseChildren);
    const actionRef = useRef<HTMLDivElement>(null);
    const hasOverflow = (actionRef?.current?.querySelectorAll('button[class*="OverflowButton"]') || []).length > 0;
    const actualTitle = usePageTitle ? currentPage.fields.title : title;
    const hasChildren = Boolean(Children.count(children));

    const titleProps = useMemo(() => {
        return mergeProps<HeadingStyles["title"]>(styles.title, {
            forwardAs: hasChildren ? "div" : forwardAs,
            css: css`
                text-align: ${textAlign};
            `,
        });
    }, [forwardAs, hasChildren, textAlign, styles.title]);

    const flexWrapperProps = useMemo(() => {
        if (!verticalAlign && !textAlign && !hasOverflow) {
            return styles.flexWrapper;
        }

        return mergeProps<HeadingStyles["flexWrapper"]>(styles.flexWrapper, {
            extendedStyles: {
                content: {
                    css: css`
                        ${cssProp("align-items", verticalAlign, getFlexAlignment)}
                        ${hasOverflow ? "flex-wrap: nowrap;" : null}
                    `,
                },
                item: {
                    css: css`
                        ${cssProp("justify-content", textAlign, getFlexAlignment)}
                        ${hasOverflow ? "&:nth-child(2) { flex-shrink: 0; }" : null}
                    `,
                },
            },
        });
    }, [styles.flexWrapper, verticalAlign, textAlign, hasOverflow]);

    const itemFlexWrapperProps = useMemo(() => {
        if (!verticalAlign || verticalAlign === "middle" || typeof itemOffset !== "number") {
            return styles.itemFlexWrapper;
        }

        return mergeProps<HeadingStyles["itemFlexWrapper"]>(styles.itemFlexWrapper, {
            extendedStyles: {
                wrapper: {
                    css: css`
                        &::${verticalAlign === "top" ? "before" : "after"} {
                            content: "\00a0";
                            margin-top: -1em;
                            font-size: ${itemOffset}em;
                        }
                    `,
                },
            },
        });
    }, [verticalAlign, itemOffset, styles.itemFlexWrapper]);

    const headingContent = useMemo(() => {
        if (image?.src) {
            return (
                <>
                    <LazyImage {...styles.image} src={image.src} altText={image.altText} aria-hidden="true" />
                    <VisuallyHidden>{actualTitle}</VisuallyHidden>
                </>
            );
        }

        return actualTitle;
    }, [image?.src, image?.altText, actualTitle, styles.image]);

    const heading = useMemo(() => {
        if (link?.href || link?.onClick) {
            return (
                <Link
                    {...styles.link}
                    href={!link.onClick ? link.href : undefined}
                    target={link.target}
                    onClick={link.onClick}
                >
                    {headingContent}
                </Link>
            );
        }

        return headingContent;
    }, [link?.href, link?.onClick, link?.target, headingContent, styles.link]);

    return (
        <Typography
            {...titleProps}
            variant={variant}
            className={className}
            id={!hasChildren ? htmlAttributes.id : undefined}
        >
            {hasChildren ? (
                <FlexWrapper {...flexWrapperProps}>
                    <Typography forwardAs={forwardAs || (variant as any)} id={htmlAttributes.id}>
                        {heading}
                    </Typography>
                    <FlexWrapper {...itemFlexWrapperProps} ref={actionRef}>
                        {children}
                    </FlexWrapper>
                </FlexWrapper>
            ) : (
                heading
            )}
        </Typography>
    );
};

export default Heading;
